/**
 * Per `platform-llm-provider` capability. Singleton platform-level
 * configuration store. Currently houses one row (`settingKey =
 * 'llm-provider'`) used by workflow LLM modules when an operator
 * opts into `apiKeySource: 'platform'`. Designed as a generic
 * key/value bag so future global settings (rate-limits, branding,
 * incident broadcast text, etc.) can live alongside without a new
 * table.
 */
import { z } from 'zod';
/**
 * Server-side schema — what we actually store in DDB. The plaintext
 * apiKey lives here; the REST GET handler masks it before responding
 * (see `PlatformLlmProviderRedactedSchema`).
 */
export const PlatformLlmProviderSchema = z.object({
    settingKey: z.literal('llm-provider'),
    /** Provider id. v1 supports openai-compatible only. */
    provider: z.enum(['openai', 'custom']).default('openai'),
    /** Plaintext API key. NEVER include this in any operator-facing
     *  response — REST handlers must mask before returning. */
    apiKey: z.string().min(1).max(200),
    /** Empty = use OpenAI default (https://api.openai.com/v1). When set,
     *  must be a fully-qualified URL. */
    baseUrl: z.string().max(500).default(''),
    /** Default model id used when a workflow module doesn't override. */
    defaultModel: z.string().min(1).max(120).default('gpt-4o-mini'),
    updatedAt: z.string().datetime(),
    updatedBy: z.string().min(1),
});
/**
 * Operator-facing redacted view returned by GET. The apiKey is replaced
 * with `••••••••<last4>` so the UI can show "key is set" without ever
 * revealing the plaintext. Empty string when no key has ever been set.
 */
export const PlatformLlmProviderRedactedSchema = z.object({
    settingKey: z.literal('llm-provider'),
    provider: z.enum(['openai', 'custom']),
    /** Masked: 8 bullets + last 4 chars of the real key, OR '' when unset. */
    apiKeyMasked: z.string(),
    baseUrl: z.string(),
    defaultModel: z.string(),
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().nullable(),
});
/**
 * PUT body — apiKey is OPTIONAL. When omitted OR sent as an empty
 * string, the server keeps the existing stored key. When sent as
 * non-empty string, it REPLACES. Same write-only-when-typed pattern
 * used elsewhere (voice provider tokens etc.).
 */
export const PlatformLlmProviderPutSchema = z.object({
    provider: z.enum(['openai', 'custom']).optional(),
    apiKey: z.string().max(200).optional(),
    baseUrl: z.string().max(500).optional(),
    defaultModel: z.string().min(1).max(120).optional(),
});
/** Pure helper: convert plaintext key to masked tail. */
export function maskApiKey(plain) {
    if (!plain)
        return '';
    const tail = plain.length >= 4 ? plain.slice(-4) : plain;
    return `••••••••${tail}`;
}
// ── Topup packages (ecpay-topup capability) ─────────────────────────
/**
 * One top-up package the fan can buy. Stored as part of a singleton
 * document in `nx-platform-settings-{env}` under settingKey
 * 'topup-packages' — superadmin edits the whole list at once via
 * batch PUT.
 *
 * Bonus is implied by `points - amountNtd` (no separate field) so
 * adjusting the bonus is a one-number change.
 */
export const TopupPackageSchema = z.object({
    /** Lowercase-kebab, immutable once created. Used as MerchantTradeNo prefix discriminator. */
    sku: z.string().regex(/^[a-z0-9-]+$/, 'sku must be lowercase kebab').min(1).max(40),
    /** Integer NTD charged via ECPay. */
    amountNtd: z.number().int().min(1).max(100000),
    /** Integer points credited to fan platform wallet (incl. bonus). */
    points: z.number().int().min(1).max(1000000),
    /** Render order ascending. */
    displayOrder: z.number().int().min(0).max(999),
    /** False = hidden from fans (admin can still see). */
    active: z.boolean().default(true),
    /** Optional label like '新手包' / '熱賣'. */
    label: z.string().max(40).optional(),
    /** Optional small badge like '加碼 25%'. */
    badge: z.string().max(20).optional(),
});
export const TopupPackageListSchema = z.array(TopupPackageSchema).max(20);
/**
 * Settings row that holds the topup-packages list. Stored at:
 *   PK: settingKey = 'topup-packages'
 *   value: { items: TopupPackage[] }
 */
export const TopupPackagesSettingSchema = z.object({
    settingKey: z.literal('topup-packages'),
    items: TopupPackageListSchema,
    updatedAt: z.string().datetime(),
    updatedBy: z.string().min(1),
});
/** Default seed — initial packages on first deploy. */
export const DEFAULT_TOPUP_PACKAGES = [
    { sku: 'pkg-100', amountNtd: 100, points: 100, displayOrder: 1, active: true },
    { sku: 'pkg-300', amountNtd: 300, points: 330, displayOrder: 2, active: true, badge: '加碼 10%' },
    { sku: 'pkg-600', amountNtd: 600, points: 750, displayOrder: 3, active: true, badge: '加碼 25%' },
    { sku: 'pkg-1000', amountNtd: 1000, points: 1250, displayOrder: 4, active: true, label: '熱賣', badge: '加碼 25%' },
    { sku: 'pkg-3000', amountNtd: 3000, points: 4000, displayOrder: 5, active: true, badge: '🔥 加碼 33%' },
];
